import { format } from 'date-fns'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
} from 'types/assetModel/assetModelEnums'
import {
  assetModelFormFields,
  GeckoAssetModel,
} from 'types/entries/geckoAssetModel'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  RolodexEntryJson,
  RolodexEntryPutJson,
} from 'types/rolodex/rolodexEntries'
import { assetModelPrecision } from 'utils/constants'
import { parseDate, parseNumber } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

import { BaseAssetModelData } from './baseAssetModelData'
import { LegacyBoilerWallsJSON } from './components/legacyBoiler/geckoLegacyBoilerWalls'

enum BoilerType {
  PulverizedCoal = 'pulverized_coal',
  CirculatingFluidizedBed = 'circulating_fluidized_bed',
  Unknown = 'unknown',
}

export type GeckoLegacyBoilerAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.LegacyBoiler
  installation_date: string | undefined
  components: Record<string, LegacyBoilerWallsJSON>
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
  boiler_type: string | undefined
  max_operating_pressure: number | undefined
  max_operating_temperature: number | undefined
}

export const LegacyBoilerAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.LegacyBoilerWalls,
]

export const NewGeckoLegacyBoilerAssetModelJSON: GeckoLegacyBoilerAssetModelJSON =
  {
    name: 'New Asset Model Boiler',
    type: GeckoAssetModelType.LegacyBoiler,
    installation_date: undefined,
    components: {},
    effective_date: format(Date.now(), 'yyyy-MM-dd'),
    compass_orientation: undefined,
    plant_north_compass_orientation: undefined,
    boiler_type: undefined,
    max_operating_pressure: undefined,
    max_operating_temperature: undefined,
  }

export class GeckoLegacyBoilerAssetModel extends GeckoAssetModel<
  BaseAssetModelData & {
    boilerType: BoilerType | undefined
    maxOperatingPressure: GeckoMeasurement | undefined
    maxOperatingTemperature: GeckoMeasurement | undefined
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(json)
    const recordData = json.data as Record<string, unknown>
    const parsedMaxOperatingPressure = parseNumber(
      recordData.max_operating_pressure
    )
    this.data.boilerType = recordData.boiler_type
      ? (recordData.boiler_type as BoilerType)
      : undefined
    if (parsedMaxOperatingPressure !== undefined) {
      this.data.maxOperatingPressure = GeckoMeasurement.CreateFromValue(
        parsedMaxOperatingPressure,
        GeckoMeasurementUnit.Megapascals
      )
    }
    if (recordData.max_operating_temperature) {
      this.data.maxOperatingTemperature = GeckoMeasurement.CreateFromValue(
        recordData.max_operating_temperature as number,
        GeckoMeasurementUnit.Celsius
      )
    }
  }

  public static CreateFromJSON(
    json: RolodexEntryJson
  ): GeckoLegacyBoilerAssetModel {
    return new GeckoLegacyBoilerAssetModel(json)
  }

  public asJSON(): GeckoLegacyBoilerAssetModelJSON {
    return generateLegacyBoilerAssetModel(
      this.data.name,
      this.data.installation_date,
      this.raw.data.components as Record<string, LegacyBoilerWallsJSON>,
      this.data.effective_date,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation,
      this.data.boilerType as string,
      this.data.maxOperatingPressure as GeckoMeasurement,
      this.data.maxOperatingTemperature as GeckoMeasurement
    )
  }
}

export function generateLegacyBoilerAssetModel(
  name: string,
  installation_date: Date | undefined,
  components: Record<string, LegacyBoilerWallsJSON>,
  effective_date: Date,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined,
  boiler_type: string | undefined,
  max_operating_pressure: GeckoMeasurement | undefined,
  max_operating_temperature: GeckoMeasurement | undefined
): GeckoLegacyBoilerAssetModelJSON {
  const boilerMaxOperatingPressure =
    max_operating_pressure !== undefined
      ? max_operating_pressure
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  const boilerMaxOperatingTemperature = max_operating_temperature
    ? max_operating_temperature
        .convertTo(GeckoMeasurementUnit.Celsius)
        .toPrecision(assetModelPrecision).num
    : undefined
  const boilerInstallationDate = installation_date
    ? installation_date.toISOString().split('T')[0]
    : undefined

  const boilerffectiveDate = effective_date.toISOString().split('T')[0]

  return {
    name: name,
    type: GeckoAssetModelType.LegacyBoiler,
    installation_date: boilerInstallationDate,
    components: components,
    effective_date: boilerffectiveDate,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
    boiler_type: boiler_type,
    max_operating_pressure: boilerMaxOperatingPressure,
    max_operating_temperature: boilerMaxOperatingTemperature,
  }
}

export const legacyBoilerModelFormFields = (
  assetModelJSON: GeckoLegacyBoilerAssetModelJSON
) => {
  const assetModelFields = assetModelFormFields(assetModelJSON)
  const legacyBoilerModelFormFields = [
    {
      dataKey: 'boiler_type',
      label: 'Boiler Type',
      value: assetModelJSON.boiler_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: [
        {
          label: 'Unknown',
          key: BoilerType.Unknown,
          value: BoilerType.Unknown,
        },
        {
          label: 'Pulverized Coal',
          key: BoilerType.PulverizedCoal,
          value: BoilerType.PulverizedCoal,
        },
        {
          label: 'Circulating Fluidized Bed',
          key: BoilerType.CirculatingFluidizedBed,
          value: BoilerType.CirculatingFluidizedBed,
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    {
      dataKey: 'max_operating_pressure',
      label: 'Max Operating Pressure',
      value: assetModelJSON.max_operating_pressure,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'max_operating_temperature',
      label: 'Max Operating Temperature',
      value: assetModelJSON.max_operating_temperature as number,
      unit_magnitude: UnitMagnitude.FahrenheitOrCelsius,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
  return [...assetModelFields, ...legacyBoilerModelFormFields]
}

export const LegacyBoilerJSONFromFormFields = (
  assetModelJSON: GeckoLegacyBoilerAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedMaxOperatingPressure = parseNumber(
    getFormFieldByDataKey(formFields, 'max_operating_pressure')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const parsedMaxOperatingTemperature = parseNumber(
    getFormFieldByDataKey(formFields, 'max_operating_temperature')?.value
  )
  const boilerType = getFormFieldByDataKey(formFields, 'boiler_type')
  return generateLegacyBoilerAssetModel(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    assetModelJSON.components,
    parseDate(assetModelJSON.effective_date) as Date,
    parseNumber(
      getFormFieldByDataKey(formFields, 'compass_orientation')?.value
    ),
    parseNumber(
      getFormFieldByDataKey(formFields, 'plant_north_compass_orientation')
        ?.value
    ),
    boilerType?.value !== BoilerType.Unknown
      ? (boilerType?.value as string)
      : undefined,
    parsedMaxOperatingPressure !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedMaxOperatingPressure,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    parsedMaxOperatingTemperature !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedMaxOperatingTemperature as number,
          GeckoMeasurementUnit.Celsius
        )
      : undefined
  )
}

export function regenerateLegacyBoilerWallsAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const rawAssetModel = assetModel.raw

  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }

  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.LegacyBoilerWalls:
        regeneratedComponents[componentID] =
          regenerateLegacyBoilerWallComponent(component)
        break
      default:
        throw new Error(
          'Unable to determine this type of Legacy Boiler Component!'
        )
    }
  }

  assetModel.raw.data.components = regeneratedComponents

  return assetModel.raw
}

export function regenerateLegacyBoilerWallComponent(
  legacyBoilerWalls: Record<string, unknown>
): Record<string, unknown> {
  return legacyBoilerWalls
}
